
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { sponsorshipTiers } from '@/data/sponsor/sponsorshipTiers';
import { CheckIcon, UserGroupIcon } from '@heroicons/react/24/outline';

interface SponsorshipTiersProps {
  onOpenContact: (tierName: string) => void;
}

const SponsorshipTiers = ({ onOpenContact }: SponsorshipTiersProps) => {
  return (
    <div className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Advisor Platform Licensing
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Transparent annual platform pricing based on the number of households you serve.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {sponsorshipTiers.map((tier, index) => (
            <Card key={index} className="relative border-border/50 hover:border-primary/20 transition-all duration-300 flex flex-col">
              <CardHeader className="text-center pb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <UserGroupIcon className="w-5 h-5 text-primary" />
                  <Badge variant="secondary">{tier.seats} households</Badge>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-1">{tier.name}</h3>
                <p className="text-sm text-muted-foreground mb-1">{tier.subtitle}</p>
                <p className="text-muted-foreground mb-4">{tier.description}</p>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-foreground">{tier.price}</span>
                  {tier.period && <span className="text-muted-foreground ml-1">{tier.period}</span>}
                </div>
              </CardHeader>
              <CardContent className="pt-0 flex flex-col flex-1">
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {tier.ctaUrl ? (
                  <Button
                    className="w-full mt-auto"
                    variant="default"
                    asChild
                  >
                    <a href={tier.ctaUrl} target="_blank" rel="noopener noreferrer">
                      {tier.cta}
                    </a>
                  </Button>
                ) : (
                  <Button
                    className="w-full mt-auto"
                    variant="outline"
                    onClick={() => onOpenContact(tier.name)}
                  >
                    {tier.cta}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SponsorshipTiers;
